import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  InputFieldComponent,
  ButtonComponent,
  SelectComponent,
  CheckboxComponent,
  SnackbarService,
  ModalService,
} from '@mic-ac/ui-kit';

import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthServiceService } from '../../services/auth-service/auth-service.service';
import { HOME_ROUTE } from '../../../app-route-paths';

import { Register } from '../../../shared/models/utente';
import { ApiEnteProprietario } from '../../../shared/models/api-collection.models';
import { NuovoEnteModalComponent } from './nuovo-ente-modal.component';

/** Allineato al breakpoint burger della navbar */
const NUOVO_ENTE_MOBILE_MAX_WIDTH = 1024;


@Component({
    selector: 'app-register',
    imports: [ReactiveFormsModule, ButtonComponent, InputFieldComponent, SelectComponent, CheckboxComponent, RouterLink],
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit {

  fb = inject(FormBuilder);
  formRegister!: FormGroup;
  otpStage = signal(false);
  email = signal('');
  emailLocked = signal(false);

  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #registerService = inject(AuthServiceService);
  #snackbar = inject(SnackbarService);
  #modalService = inject(ModalService);

  enteOptions = signal<ApiEnteProprietario[]>([]);
  enteLoading = signal(false);

  onEnteSearch(value: string): void {
    if (!value || value.length < 3) {
      this.enteOptions.set([]);
      return;
    }

    this.enteLoading.set(true);
    this.#registerService.suggestionEnte(value).subscribe({
      next: (res: any) => {
        const options = (res?.elements || res || []);
        this.enteOptions.set(options);
        this.enteLoading.set(false);
      },
      error: () => {
        this.enteLoading.set(false);
        this.enteOptions.set([]);
      }
    });
  }

  ngOnInit(): void {
    this.formRegister = this.fb.group({
      nome: ['', Validators.required],
      cognome: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      ente: [null, Validators.required],
      ruolo: ['', Validators.required],
      privacy: [false, Validators.requiredTrue],
    });

    const params = this.#route.snapshot.queryParamMap;
    if (params.get('otpStage') === 'true') {
      const emailParam = params.get('email') ?? '';
      this.email.set(emailParam);
      this.formRegister = this.fb.group({
        otp: ['', Validators.required]
      });
      this.otpStage.set(true);
      return;
    }

    if (this.#registerService.iamOnlyUser()) {
      const iamEmail = this.#registerService.getIamEmail();
      if (iamEmail) {
        this.emailLocked.set(true);
        this.formRegister.patchValue({ email: iamEmail });
        this.formRegister.get('email')?.disable({ emitEvent: false });
      }
    }

    this.applyNuovoEnteFromNavigation();
  }

  private applyNuovoEnteFromNavigation(): void {
    const ente = history.state?.['nuovoEnte'] as ApiEnteProprietario | undefined;
    if (!ente) return;
    this.notifyNuovoEnteCreated(ente);
  }

  private notifyNuovoEnteCreated(ente: ApiEnteProprietario): void {
    const nomeEnte = ente.nominativo || ente.nome;
    this.#snackbar.show(
      `Ente "${nomeEnte}" aggiunto. Ricercalo nel campo "Ente" per selezionarlo.`,
      'success',
    );
  }

  openNuovoEnte(): void {
    if (window.matchMedia(`(max-width: ${NUOVO_ENTE_MOBILE_MAX_WIDTH}px)`).matches) {
      this.#router.navigate(['/register/nuovo-ente']);
      return;
    }
    void this.openNuovoEnteModal();
  }

  private async openNuovoEnteModal(): Promise<void> {
    const { result } = await this.#modalService.open(NuovoEnteModalComponent as any);
    const ente = (await result) as unknown as ApiEnteProprietario | null;
    if (!ente) return;
    this.notifyNuovoEnteCreated(ente);
  }

  goBack() {
    this.#router.navigate([HOME_ROUTE]);
  }

  register() {
    const formValue = this.formRegister.getRawValue();
    const ruolo = typeof formValue.ruolo === 'string' ? formValue.ruolo.trim() : formValue.ruolo;

    if (!ruolo) {
      this.#snackbar.show('Inserisci o seleziona un ruolo valido', 'error');
      return;
    }

    const enteId = formValue.ente;
    if (typeof enteId !== 'number') {
      this.#snackbar.show('Seleziona un ente valido dalla lista', 'error');
      return;
    }

    const data: Register = {
      nome: formValue.nome,
      cognome: formValue.cognome,
      email: formValue.email,
      idEnte: enteId,
      ruolo,
    };

    this.#registerService.register(data).subscribe({
      next: () => {
        this.email.set(formValue.email);
        this.otpStage.set(true);
        this.formRegister = this.fb.group({
          otp: ['', Validators.required]
        });
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Errore durante la registrazione. Riprova più tardi.';
        this.#snackbar.show(message, 'error');
      },
    });
  }

  verifyOtp() {
    const otp = this.formRegister.get('otp')?.value;

    this.#registerService.verifyOtp(otp, this.email()).subscribe({
      next: () => {
        const nextStep = this.#registerService.completeRegistrationAfterOtp();
        if (nextStep === 'home') {
          this.#router.navigate(['/mp/my-area']);
        }
      },
      error: (err: any) => {
        const message = err?.error?.message || 'OTP non valido. Verifica il codice e riprova.';
        this.#snackbar.show(message, 'error');
      },
    });
  }

  requestOtp() {
    this.#registerService.resendOtp(this.email()).subscribe({
      next: () => this.#snackbar.show('OTP rinviato con successo', 'success'),
      error: () => this.#snackbar.show('Errore nel rinvio dell\'OTP. Riprova più tardi.', 'error'),
    });
  }
}
